import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "grid grid-cols max-w-[1144px] max-w-176 w-full items-start self-stretch" }
const _hoisted_2 = { class: "flex gap-y-1" }

import { ref, computed } from 'vue';
import { computedAsync } from '@vueuse/core';
import {
  InsurancePolicy,
  ISitecoreFormGenericInput,
  PetCoverage,
  ResidentialCoverage,
  VehicleCoverage,
  getInsuranceTypeDisplayName,
} from '@/interfaces';
import { useI18n } from 'vue-i18n';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import ActionBar from '@/components/ActionBar/ActionBar.vue';
import Skeleton from '@/components/Skeleton/Skeleton.vue';
import ModalDialog from '@/components/ModalDialog/ModalDialog.vue';
import SelectCoverageOption from '@/components/SelectCoverageOption/SelectCoverageOption.vue';

import { useAccountStore } from '@/store';
import { getFormInput, getFormInputByName } from '@/utils/form-input-helper';
import { SelfServiceService } from '@/services/store/self-service-service';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { getActionBarDisplayDetails } from '@/utils/self-service-rules/InsuranceActionBarRules';
import { scrollToElement } from '@/utils/dom-helpers';
import { getCurrentPolicy } from './ProductDetailsHelper';
import { toProperCase } from '@/utils/text-transforms';
import { getInsuranceCoverageTypeToUseForIcon, showCombined } from '@/utils/common-helper';
import { QuoteTypeCodes, InsuranceQuoteType } from '@/interfaces/insurance-quote';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { FormEvents } from '@/enums/form-events.enum';
import { getIconByCoverageCode } from '@/utils/self-service-rules/InsuranceIconHelper';
import MotorChangeRegistrationModal from './MotorChangeRegistrationModal.vue';

enum AllActionButtons {
  btnPaymentDetails = 'btnPaymentDetails',
  btnMakeClaim = 'btnMakeClaim',
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductDetailsHeader',
  props: {
    content: {},
    id: {},
    name: {},
    testPolicyNumber: {}
  },
  setup(__props: any) {

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;

const accountStore = useAccountStore();

const props = __props;

const currentPolicy = ref<InsurancePolicy>();
const combinedPolicy = ref<any>(getFormInput(props.content, 'combinedPolicy'));
const actionBarContent = getFormInput(props.content, 'actionBar');
const isLoading = ref<boolean>(!props?.testPolicyNumber);
const actionBarDisplayResult = ref<any>();

const productIcons = ref<any>(getFormInputByName(props.content, 'productIcons'));
const selectVehicleModal = ref<any>({
  content: getFormInputByName(props.content, 'selectVehicleModal'),
  model: '',
  modalIsOpen: false,
  linkItem: null,
});
const selectPetModal = ref<any>({
  content: getFormInputByName(props.content, 'selectPetModal'),
  model: '',
  modalIsOpen: false,
  linkItem: null,
});
const changeRegistrationContent = getFormInput(props.content, 'changeRegistration');

const filteredFields = computedAsync(async () => {
  if (!currentPolicy?.value && accountStore?.identityDetails?.portfolio?.insurancePolicies) {
    currentPolicy.value = getCurrentPolicy(
      accountStore.identityDetails.portfolio.insurancePolicies,
      props.testPolicyNumber
    );
  }

  if (currentPolicy?.value) {
    const selfServiceToggles = await SelfServiceService.getSelfServiceToggles();
    actionBarDisplayResult.value = await getActionBarDisplayDetails(currentPolicy.value, selfServiceToggles);

    const validActionButtons = getValidActionButtons(actionBarDisplayResult.value);

    const tempFilteredFields = {
      fields: actionBarContent.fields.filter((x: any) => validActionButtons.includes(x.name)),
    };

    if (!props.testPolicyNumber) {
      //Update dataTestId for analytics purposes.
      tempFilteredFields.fields.forEach((button: any) => {
        button.dataTestId = {
          value: `${button.name}-${insuranceDisplayText.value.title.replaceAll(' ', '')}`,
        };
      });
    }

    isLoading.value = false;
    return tempFilteredFields;
  }

  return null;
});

const insuranceDisplayText = computed(() => {
  let displayDetails = {
    title: '',
    subTitle: '',
    icon: '',
  };

  if (currentPolicy.value) {
    const shouldShowCombined = showCombined(currentPolicy.value?.coverages ?? []);
    const useDisplayText = shouldShowCombined
      ? combinedPolicy.value?.label?.value
      : getInsuranceTypeDisplayName(dictionary, currentPolicy.value.coverages[0]?.coverageCode);
    displayDetails.title = useDisplayText;
    const coverageCount = currentPolicy.value?.coverages?.length;
    const firstCoverage = currentPolicy.value.coverages[0];
    const useCoverageCode = getInsuranceCoverageTypeToUseForIcon(currentPolicy.value);
    displayDetails.icon = getIconByCoverageCode(useCoverageCode);

    if (coverageCount > 1) {
      displayDetails.subTitle = currentPolicy.value.coverages.length.toString();

      if (firstCoverage.isHome) {
        displayDetails.subTitle = `${coverageCount} properties`;
      } else if (
        firstCoverage.isMotor ||
        firstCoverage.coverageCode === QuoteTypeCodes[InsuranceQuoteType.Motorcycle]
      ) {
        displayDetails.subTitle = `${coverageCount} vehicles`;
      } else if (firstCoverage.isPet) {
        displayDetails.subTitle = `${coverageCount} pets`;
      } else if (firstCoverage.isBoat) {
        displayDetails.subTitle = `${coverageCount} boats`;
      } else if (firstCoverage.coverageCode === QuoteTypeCodes[InsuranceQuoteType.Caravan]) {
        displayDetails.subTitle = shouldShowCombined ? `${coverageCount} vehicles` : `${coverageCount} caravans`;
      }
    } else {
      if (firstCoverage.isHome) {
        const homeCoverage = firstCoverage as ResidentialCoverage;
        displayDetails.subTitle = homeCoverage.riskAddress.formattedLong;
      } else if (
        firstCoverage.isMotor ||
        [QuoteTypeCodes[InsuranceQuoteType.Caravan], QuoteTypeCodes[InsuranceQuoteType.Motorcycle]].includes(
          firstCoverage.coverageCode
        )
      ) {
        const vehicleCoverage = firstCoverage as VehicleCoverage;
        displayDetails.subTitle = vehicleCoverage.vehicle.formattedSummary;
      } else if (firstCoverage.isPet) {
        const petCoverage = firstCoverage as PetCoverage;
        displayDetails.subTitle = `${petCoverage.petName}, ${petCoverage.petBreed}`;
      } else if (firstCoverage.isBoat) {
        const boatCoverage = firstCoverage as VehicleCoverage;
        displayDetails.subTitle = boatCoverage.vehicle.formattedSummary;
      }
    }
  }

  displayDetails.subTitle = toProperCase(displayDetails.subTitle) ?? '';

  return displayDetails;
});

const getValidActionButtons = (actionBarDisplayResult: any) => {
  let showActionButtons = [];

  // Always show Payment Details - SSINSP.FR1.2
  showActionButtons.push(AllActionButtons.btnPaymentDetails);

  if (actionBarDisplayResult.showMakeClaim || actionBarDisplayResult.showMakeClaimVehicleMultiple) {
    showActionButtons.push(AllActionButtons.btnMakeClaim);
  }

  return showActionButtons;
};

const pushModalAnalytics = async () => {
  if (!currentPolicy?.value) return;

  const isAuthenticated = await accountStore.isAuthenticated;
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateModalStart(
    FormEvents.START,
    isAuthenticated,
    'ProductDetails',
    'Combined policy claim modal',
    currentPolicy.value.termEnd ?? undefined,
    accountStore.identityDetails?.d365Id
  );
  pageAnalytics.pushAnalytics();
};

const onClick_ActionBarButton = async (linkItem: any) => {
  if (!currentPolicy?.value) return;
  switch (linkItem.name) {
    case AllActionButtons.btnPaymentDetails:
      scrollToElement(linkItem.link.value.href, 500, -100);
      break;

    case AllActionButtons.btnMakeClaim:
      if (currentPolicy.value.coverages.length > 1) {
        await pushModalAnalytics();

        if (currentPolicy.value.coverages[0].coverageCode == QuoteTypeCodes[InsuranceQuoteType.PetComp]) {
          selectPetModal.value.model = '';
          selectPetModal.value.linkItem = linkItem;
          selectPetModal.value.modalIsOpen = true;
        } else {
          selectVehicleModal.value.model = '';
          selectVehicleModal.value.linkItem = linkItem;
          selectVehicleModal.value.modalIsOpen = true;
        }
      } else {
        handleAvokaMakeClaim(currentPolicy.value?.coverages[0] as VehicleCoverage, linkItem?.link?.value?.href);
      }

      break;
  }
};

const handleAvokaMakeClaim = async (coverage: VehicleCoverage | PetCoverage, linkUrl: string) => {
  if (!currentPolicy?.value) return;
  const response = await SelfServiceService.initializeAvokaForm(currentPolicy.value, coverage);
  if (response?.status === 'success') {
    let redirectUrl = linkUrl;
    redirectUrl = redirectUrl.replace('{0}', response.submitKey);
    redirectUrl = redirectUrl.replace('{1}', response.token);
    window.location.href = redirectUrl;
  }
};

const selectVehicleModal_Next = () => {
  selectVehicleModal.value.modalIsOpen = false;

  const coverageId = selectVehicleModal.value.model;
  const coverage = currentPolicy.value?.coverages.find((x) => x.id === coverageId);

  if (coverage) {
    handleAvokaMakeClaim(coverage as VehicleCoverage, selectVehicleModal.value.linkItem?.link?.value?.href);
  }
};

const selectPetModal_Next = () => {
  selectPetModal.value.modalIsOpen = false;

  const coverageId = selectPetModal.value.model;
  const coverage = currentPolicy.value?.coverages.find((x) => x.id === coverageId);

  if (coverage) {
    handleAvokaMakeClaim(coverage as PetCoverage, selectPetModal.value.linkItem?.link?.value?.href);
  }
};

function selectVehicleModal_Close() {
  selectVehicleModal.value.modalIsOpen = false;
}

function selectPetModal_Close() {
  selectPetModal.value.modalIsOpen = false;
}

const skeletonBlocks = [
  {
    height: 26,
    width: 338,
    class: 'mb-3',
  },
  {
    height: 26,
    width: 176,
    class: 'mb-3',
  },
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (!isLoading.value)
        ? (_openBlock(), _createBlock(FormStepInputs, {
            key: 0,
            step: {
        stepName: _ctx.name,
        formInputs: _ctx.content,
      }
          }, {
            insurancePanelTitle: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                (!isLoading.value)
                  ? (_openBlock(), _createBlock(PanelTitle, _mergeProps({ key: 0 }, _unref(getFormInput)(props.content, 'insurancePanelTitle'), {
                      title: { value: insuranceDisplayText.value?.title },
                      subTitle: { value: insuranceDisplayText.value?.subTitle },
                      icon: { value: { src: insuranceDisplayText.value.icon, alt: 'icon' } }
                    }), null, 16, ["title", "subTitle", "icon"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            actionBar: _withCtx(() => [
              (_unref(filteredFields))
                ? (_openBlock(), _createBlock(ActionBar, _mergeProps({ key: 0 }, _unref(filteredFields), {
                    outerClass: _unref(actionBarContent).outerClass,
                    innerClass: _unref(actionBarContent).innerClass,
                    handleLinkClickEvent: true,
                    onBtnClick: _cache[0] || (_cache[0] = (linkName) => onClick_ActionBarButton(linkName))
                  }), null, 16, ["outerClass", "innerClass"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["step"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(ModalDialog, {
      modelValue: selectVehicleModal.value.modalIsOpen,
      dataTestId: "selectVehicleModal",
      showXCloseButton: true,
      closeOnEsc: true,
      size: "md",
      "onUpdate:modelValue": selectVehicleModal_Close
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(SelectCoverageOption, {
          key: `vehicle-modal-{$selectVehicleModal.modalIsOpen}`,
          modelValue: selectVehicleModal.value.model,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectVehicleModal.value.model) = $event)),
          dataTestId: "selectVehicleModal_MakeAClaim",
          coverages: currentPolicy.value?.coverages ?? [],
          fields: {
        title: selectVehicleModal.value?.content?.modalTitle,
        subtitle: selectVehicleModal.value?.content?.modalBody,
        homeIcon: productIcons.value?.homeIcon,
        homeContentsIcon: productIcons.value?.homeContentsIcon,
        contentsIcon: productIcons.value?.contentsIcon,
        motorIcon: productIcons.value?.motorIcon,
        motorThirdPartyIcon: productIcons.value?.motorThirdPartyIcon,
        motorThirdPartyFireIcon: productIcons.value?.motorThirdPartyFireIcon,
        boatIcon: productIcons.value?.boatIcon,
        caravanIcon: productIcons.value?.caravanIcon,
        motorcycleIcon: productIcons.value?.motorcycleIcon,
        petIcon: productIcons.value?.petIcon,
        modalBtnText: selectVehicleModal.value?.content?.callToActionButtonText,
      },
          disableCoverageCodes: [
        _unref(QuoteTypeCodes)[_unref(InsuranceQuoteType).Motorcycle],
        _unref(QuoteTypeCodes)[_unref(InsuranceQuoteType).Caravan],
      ],
          disabledCoveragesMessage: _unref(getFormInput)(props.content, 'selectVehicleModalDisabledOptions'),
          onButtonClicked: selectVehicleModal_Next
        }, null, 8, ["modelValue", "coverages", "fields", "disableCoverageCodes", "disabledCoveragesMessage"]))
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(ModalDialog, {
      modelValue: selectPetModal.value.modalIsOpen,
      dataTestId: "selectPetModal",
      showXCloseButton: true,
      closeOnEsc: true,
      size: "md",
      "onUpdate:modelValue": selectPetModal_Close
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(SelectCoverageOption, {
          key: `vehicle-modal-{$selectPetModal.modalIsOpen}`,
          modelValue: selectPetModal.value.model,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectPetModal.value.model) = $event)),
          dataTestId: "selectPetModal_MakeAClaim",
          coverages: currentPolicy.value?.coverages ?? [],
          fields: {
        title: selectPetModal.value?.content?.modalTitle,
        subtitle: selectPetModal.value?.content?.modalBody,
        homeIcon: productIcons.value?.homeIcon,
        homeContentsIcon: productIcons.value?.homeContentsIcon,
        contentsIcon: productIcons.value?.contentsIcon,
        motorIcon: productIcons.value?.motorIcon,
        motorThirdPartyIcon: productIcons.value?.motorThirdPartyIcon,
        motorThirdPartyFireIcon: productIcons.value?.motorThirdPartyFireIcon,
        boatIcon: productIcons.value?.boatIcon,
        caravanIcon: productIcons.value?.caravanIcon,
        motorcycleIcon: productIcons.value?.motorcycleIcon,
        petIcon: productIcons.value?.petIcon,
        modalBtnText: selectPetModal.value?.content?.callToActionButtonText,
      },
          disabledCoveragesMessage: _unref(getFormInput)(props.content, 'selectPetModalDisabledOptions'),
          onButtonClicked: selectPetModal_Next
        }, null, 8, ["modelValue", "coverages", "fields", "disabledCoveragesMessage"]))
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Skeleton, { blocks: skeletonBlocks })
    ], 512), [
      [_vShow, isLoading.value]
    ]),
    _createVNode(MotorChangeRegistrationModal, {
      policy: currentPolicy.value,
      content: _unref(changeRegistrationContent)?.fields || []
    }, null, 8, ["policy", "content"])
  ], 64))
}
}

})